import React, { useEffect, useState } from "react";
import Select from "react-select";
import {
    Button,
    Form,
    FormGroup,
    Input,
    Label,
    Modal,
    ModalBody,
    ModalHeader,
} from "reactstrap";
import dayjs from "dayjs";
import { toast } from "react-toastify";
import { fetchCommonDrugStatusOptions, fetchCommonYesNoOptions } from "model/CandidateModel";
import { invokeCandidateTestActionMultipartFormForCallDetails } from "model/CallModel";
import { mapCommonDropdownRecords, mapDrugStatusApiRecordsToSelectOptions } from "helpers/hireStatusInlineHelpers";
import {
    doesCandidateApplicationStatusTestRowRepresentDrugTest,
    resolveCandidateApplicationStatusTestTitle,
    getCandidateTestEditNode,
    resolveCandidateTestMultipartIdentifiers,
    appendCandidateTestEditEchoFieldsToFormData,
} from "helpers/callDetailsCandidateApplicationStatusTestPresentation";
import { downloadFileFromUrl } from "helpers/GeneralHelper";

const REACT_SELECT_MENU_PORTAL_STYLES = {
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    menu: (base) => ({ ...base, zIndex: 9999 }),
};

const CANDIDATE_TEST_UPDATE_ATTACHMENT_ACCEPT_ATTRIBUTE =
    ".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,image/jpeg,image/png,image/webp,image/gif";

function isSuccessfulCallDetailsMutationResponse(responsePayload) {
    return Boolean(
        responsePayload?.success ||
            responsePayload?.isSuccess ||
            responsePayload?.status === "success" ||
            responsePayload?.status === true
    );
}

/** Uses `getCandidateTestEditNode` — server `edit` snapshot for matching status options. */
function matchStatusSelectOptionByCandidateRow(statusSelectOptions, applicationStatusTestRow) {
    const optionList = Array.isArray(statusSelectOptions) ? statusSelectOptions : [];
    const editNode = getCandidateTestEditNode(applicationStatusTestRow);
    const candidateValues = [
        editNode?.status,
        editNode?.rawStatus,
        editNode?.orderStatus,
        applicationStatusTestRow?.persistedStatus,
        applicationStatusTestRow?.statusLabel,
        applicationStatusTestRow?.rawStatus,
        applicationStatusTestRow?.displayRawStatus,
    ]
        .map((item) => String(item ?? "").trim().toLowerCase())
        .filter(Boolean);

    for (const option of optionList) {
        const optionValue = String(option?.value ?? "").trim().toLowerCase();
        const optionLabel = String(option?.label ?? "").trim().toLowerCase();
        if (candidateValues.some((candidate) => candidate === optionValue || candidate === optionLabel)) {
            return option;
        }
    }
    return null;
}

function resolveInitialTestDateInputValue(applicationStatusTestRow) {
    const editNode = getCandidateTestEditNode(applicationStatusTestRow);
    const raw = editNode?.testDate ?? applicationStatusTestRow?.testDate;
    if (raw == null || String(raw).trim() === "") return "";
    const parsed = dayjs(raw);
    return parsed.isValid() ? parsed.format("YYYY-MM-DD") : "";
}

function resolveInitialAttachmentIdValue(applicationStatusTestRow) {
    const editNode = getCandidateTestEditNode(applicationStatusTestRow);
    const raw = editNode?.attachmentId ?? applicationStatusTestRow?.attachmentId;
    const value = String(raw ?? "").trim();
    return value && value.toLowerCase() !== "null" ? value : "";
}

function resolveInitialAttachmentDownloadUrl(applicationStatusTestRow) {
    const editNode = getCandidateTestEditNode(applicationStatusTestRow);
    const raw =
        editNode?.attachmentDownloadUrl ?? applicationStatusTestRow?.attachmentDownloadUrl;
    const value = String(raw ?? "").trim();
    return value && value.toLowerCase() !== "null" ? value : "";
}

/**
 * Modal to update a candidate application test.
 * **Drug test** (`DrugTest`): status options from `GET common/drug-status`.
 * **All other** categories: options from `GET common/yes-no`.
 * Submit uses the pressed action's `routeKey`, `routeArgument`, and `httpMethod` with `multipart/form-data`.
 */
function CallDetailsCandidateTestUpdateModal({
    isOpen,
    onRequestClose,
    callDetailsRouteParameterId,
    candidateTestActionBodyCandidateClientId,
    candidateTestActionBodyCandidateId,
    applicationStatusTestRow,
    actionDefinition,
    onSaveSuccess,
}) {
    const [statusSelectOptions, setStatusSelectOptions] = useState([]);
    const [statusOptionsLoading, setStatusOptionsLoading] = useState(false);
    const [selectedStatusOption, setSelectedStatusOption] = useState(null);
    const [testDateInputValue, setTestDateInputValue] = useState("");
    const [existingAttachmentId, setExistingAttachmentId] = useState("");
    const [existingAttachmentDownloadUrl, setExistingAttachmentDownloadUrl] = useState("");
    const [selectedAttachmentFile, setSelectedAttachmentFile] = useState(null);
    const [attachmentDownloadBusy, setAttachmentDownloadBusy] = useState(false);
    const [saveInProgress, setSaveInProgress] = useState(false);

    useEffect(() => {
        if (!isOpen || !applicationStatusTestRow) return;
        let cancelled = false;
        (async () => {
            setStatusOptionsLoading(true);
            try {
                const isDrugTestRow = doesCandidateApplicationStatusTestRowRepresentDrugTest(
                    applicationStatusTestRow
                );
                const commonDropdownResponse = isDrugTestRow
                    ? await fetchCommonDrugStatusOptions()
                    : await fetchCommonYesNoOptions();
                if (cancelled) return;
                const optionRecords = commonDropdownResponse?.data ?? commonDropdownResponse;
                const normalizedRecords = Array.isArray(optionRecords)
                    ? optionRecords
                    : Array.isArray(optionRecords?.data)
                      ? optionRecords.data
                      : [];
                setStatusSelectOptions(
                    isDrugTestRow
                        ? mapDrugStatusApiRecordsToSelectOptions(normalizedRecords)
                        : mapCommonDropdownRecords(normalizedRecords)
                );
            } catch {
                if (!cancelled) setStatusSelectOptions([]);
            } finally {
                if (!cancelled) setStatusOptionsLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [
        isOpen,
        applicationStatusTestRow?.wellKnownCategory,
        applicationStatusTestRow?.candidateTestId,
    ]);

    useEffect(() => {
        if (!isOpen || !applicationStatusTestRow) return;
        setTestDateInputValue(resolveInitialTestDateInputValue(applicationStatusTestRow));
        setExistingAttachmentId(resolveInitialAttachmentIdValue(applicationStatusTestRow));
        setExistingAttachmentDownloadUrl(
            resolveInitialAttachmentDownloadUrl(applicationStatusTestRow)
        );
        setSelectedAttachmentFile(null);
    }, [isOpen, applicationStatusTestRow]);

    useEffect(() => {
        if (!isOpen || !applicationStatusTestRow || statusSelectOptions.length === 0) return;
        setSelectedStatusOption(
            matchStatusSelectOptionByCandidateRow(statusSelectOptions, applicationStatusTestRow)
        );
    }, [isOpen, applicationStatusTestRow, statusSelectOptions]);

    const requirementDisplayName = applicationStatusTestRow
        ? resolveCandidateApplicationStatusTestTitle(applicationStatusTestRow)
        : "";
    const modalHeaderTitle = `UPDATE - ${String(requirementDisplayName || "TEST").toUpperCase()}`;

    const handleAttachmentFileChange = (event) => {
        const fileList = event?.target?.files;
        const firstFile = fileList && fileList.length ? fileList[0] : null;
        setSelectedAttachmentFile(firstFile || null);
    };

    const handleDownloadExistingAttachment = async () => {
        const downloadUrl = String(existingAttachmentDownloadUrl ?? "").trim();
        if (!downloadUrl) {
            toast.error("Download URL is not available for this attachment.");
            return;
        }
        setAttachmentDownloadBusy(true);
        try {
            await downloadFileFromUrl(downloadUrl);
        } finally {
            setAttachmentDownloadBusy(false);
        }
    };

    const handleModalFormSubmit = async (event) => {
        event.preventDefault();
        const callDetailsId = String(callDetailsRouteParameterId ?? "").trim();
        if (!callDetailsId) {
            toast.error("Call details reference is missing.");
            return;
        }
        if (!selectedStatusOption?.value && selectedStatusOption?.value !== 0) {
            toast.error("Please select a status.");
            return;
        }

        const { candidateClientId, jobRequireId } = resolveCandidateTestMultipartIdentifiers({
            applicationStatusTestRow,
            candidateTestActionBodyCandidateClientId,
            candidateTestActionBodyCandidateId,
        });
        if (!candidateClientId || !jobRequireId) {
            toast.error(
                "Candidate client id and job require id are required to save this test."
            );
            return;
        }

        const selectedValue = String(selectedStatusOption.value ?? "");
        const multipartFormData = new FormData();
        multipartFormData.append("candidateClientId", candidateClientId);
        multipartFormData.append("jobRequireId", jobRequireId);
        multipartFormData.append(
            "candidateTestId",
            String(applicationStatusTestRow?.candidateTestId ?? "").trim()
        );

        multipartFormData.append("status", selectedValue);
        if (doesCandidateApplicationStatusTestRowRepresentDrugTest(applicationStatusTestRow)) {
            multipartFormData.append("orderStatus", selectedValue);
        } else {
            multipartFormData.append("yesNo", selectedValue);
        }
        if (String(testDateInputValue ?? "").trim()) {
            multipartFormData.append("testDate", String(testDateInputValue).trim());
        }
        if (selectedAttachmentFile) {
            multipartFormData.append("file", selectedAttachmentFile);
        }
        if (existingAttachmentId) {
            multipartFormData.append("attachmentId", existingAttachmentId);
        }

        appendCandidateTestEditEchoFieldsToFormData(multipartFormData, applicationStatusTestRow);

        setSaveInProgress(true);
        try {
            const responsePayload = await invokeCandidateTestActionMultipartFormForCallDetails({
                candidateId: callDetailsId,
                routeKey: actionDefinition?.routeKey,
                routeArgument: actionDefinition?.routeArgument,
                formData: multipartFormData,
            });
            if (isSuccessfulCallDetailsMutationResponse(responsePayload)) {
                toast.success(responsePayload?.message || "Saved successfully.");
                await Promise.resolve(onSaveSuccess?.());
                onRequestClose?.();
            } else {
                toast.error(responsePayload?.message || "Save could not be completed.");
            }
        } finally {
            setSaveInProgress(false);
        }
    };

    if (!applicationStatusTestRow || !actionDefinition) {
        return null;
    }

    return (
        <Modal isOpen={isOpen} toggle={onRequestClose} centered size="md" className="modal-full-height">
            <ModalHeader toggle={onRequestClose}>{modalHeaderTitle}</ModalHeader>
            <ModalBody>
                <Form onSubmit={handleModalFormSubmit}>
                    <FormGroup>
                        <Label className="fw-semibold">Status</Label>
                        <Select
                            className="select2-selection"
                            name="orderStatus"
                            placeholder="Choose..."
                            options={statusSelectOptions}
                            isLoading={statusOptionsLoading}
                            isClearable
                            value={selectedStatusOption}
                            onChange={(nextOption) => setSelectedStatusOption(nextOption ?? null)}
                            menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                            styles={REACT_SELECT_MENU_PORTAL_STYLES}
                        />
                    </FormGroup>

                    <FormGroup>
                        <Label className="fw-semibold">Date</Label>
                        <Input
                            type="date"
                            name="testDate"
                            value={testDateInputValue}
                            onChange={(event) => setTestDateInputValue(event.target.value)}
                            placeholder="dd/mm/yyyy"
                        />
                    </FormGroup>

                    <FormGroup>
                        <Label className="fw-semibold">File upload (doc, pdf, docx, image)</Label>
                        <Input
                            type="file"
                            name="file"
                            accept={CANDIDATE_TEST_UPDATE_ATTACHMENT_ACCEPT_ATTRIBUTE}
                            onChange={handleAttachmentFileChange}
                        />
                        {(existingAttachmentId || existingAttachmentDownloadUrl) &&
                        !selectedAttachmentFile ? (
                            <div className="d-flex flex-wrap align-items-center gap-2 mt-2">
                                <small className="text-muted mb-0">
                                    Existing attachment will be kept unless a new file is selected.
                                </small>
                                {existingAttachmentDownloadUrl ? (
                                    <Button
                                        color="info"
                                        outline
                                        size="sm"
                                        type="button"
                                        onClick={handleDownloadExistingAttachment}
                                        disabled={attachmentDownloadBusy}
                                    >
                                        {attachmentDownloadBusy ? "Downloading…" : "Download"}
                                    </Button>
                                ) : null}
                            </div>
                        ) : null}
                    </FormGroup>

                    <div className="d-flex justify-content-end mt-3">
                        <Button color="primary" type="submit" disabled={saveInProgress}>
                            {/* <i className="mdi mdi-content-save me-1" aria-hidden /> */}
                            {saveInProgress ? "Saving…" : "Save"}
                        </Button>
                    </div>
                </Form>
            </ModalBody>
        </Modal>
    );
}

export default CallDetailsCandidateTestUpdateModal;
